import { useState, useRef, useEffect } from "react";
import { Radio, X, Play, Pause, Square, Volume2, VolumeX, ChevronDown } from "lucide-react";
import client from "@/lib/api";

function useRadio() {
  const audioRef = useRef(null);
  const [meta, setMeta] = useState({ title: "", stream_url: "" });
  const [loading, setLoading] = useState(true);
  const [playing, setPlaying] = useState(false);
  const [vol, setVol] = useState(0.8);
  const [muted, setMuted] = useState(false);

  useEffect(() => {
    client.get("/radio/latest").then((r) => setMeta(r.data || {})).catch((e) => console.error("Radio LiveGP load failed:", e)).finally(() => setLoading(false));
  }, []);
  useEffect(() => {
    if (audioRef.current) audioRef.current.volume = muted ? 0 : vol;
  }, [vol, muted, meta]);

  const toggle = async () => {
    const a = audioRef.current;
    if (!a || !meta.stream_url) return;
    if (playing) { a.pause(); setPlaying(false); }
    else { try { await a.play(); setPlaying(true); } catch (e) { /* blocked */ } }
  };
  const stop = () => { const a = audioRef.current; if (a) { a.pause(); a.currentTime = 0; setPlaying(false); } };

  const audioEl = (
    <audio ref={audioRef} src={meta.stream_url || undefined} preload="none" onEnded={() => setPlaying(false)} />
  );
  return { meta, loading, playing, toggle, stop, vol, setVol, muted, setMuted, audioEl };
}

function ExpandedPlayer({ r, onClose }) {
  return (
    <div className="fixed top-24 right-4 z-[70] w-[330px] max-w-[calc(100vw-2rem)] rounded-md overflow-hidden border-2 border-legend-ink shadow-2xl bg-legend-red animate-fade-up" data-testid="radio-popover">
      <div className="flex items-center justify-between px-3 py-2 bg-legend-ink text-white">
        <span className="inline-flex items-center gap-2 font-display font-bold uppercase text-sm tracking-wide">
          <Radio size={15} className="text-legend-yellow" /> Radio LiveGP
        </span>
        <button onClick={onClose} data-testid="radio-close" aria-label="Chiudi" className="hover:text-legend-yellow"><X size={16} /></button>
      </div>
      <div className="p-4 text-white">
        <p className="text-[11px] font-mono uppercase tracking-widest text-white/70">In onda</p>
        <p className="text-sm font-semibold leading-snug min-h-[2.5rem]" data-testid="radio-title">
          {r.loading ? "Caricamento…" : (r.meta.title || "Radio LiveGP — motorsport on air")}
        </p>
        <div className="mt-3 flex items-center gap-3">
          <button onClick={r.toggle} disabled={r.loading || !r.meta.stream_url} data-testid="radio-play-lg" aria-label={r.playing ? "Pausa" : "Avvia"} className="h-12 w-12 shrink-0 rounded-full bg-white text-legend-red flex items-center justify-center shadow hover:scale-105 active:scale-95 transition disabled:opacity-50">
            {r.playing ? <Pause size={22} /> : <Play size={22} className="ml-0.5" />}
          </button>
          <button onClick={r.stop} disabled={r.loading || !r.meta.stream_url} data-testid="radio-stop-lg" aria-label="Stop" className="h-10 w-10 shrink-0 rounded-full bg-legend-ink text-white flex items-center justify-center hover:bg-black transition disabled:opacity-50">
            <Square size={16} />
          </button>
          <button onClick={() => r.setMuted((m) => !m)} data-testid="radio-mute" aria-label="Muto" className="text-white/90 hover:text-legend-yellow">
            {r.muted || r.vol === 0 ? <VolumeX size={18} /> : <Volume2 size={18} />}
          </button>
          <input type="range" min="0" max="1" step="0.05" value={r.muted ? 0 : r.vol} onChange={(e) => { r.setVol(parseFloat(e.target.value)); r.setMuted(false); }} data-testid="radio-volume-lg" aria-label="Volume" className="flex-1 accent-legend-yellow cursor-pointer" />
        </div>
      </div>
    </div>
  );
}

export default function RadioPlayer() {
  const r = useRadio();
  const [open, setOpen] = useState(false);

  return (
    <div className="inline-flex flex-col items-center" data-testid="radio-livegp">
      <div className="inline-flex items-center gap-2 rounded-md bg-legend-red text-white border-2 border-legend-ink px-3 py-1.5 shadow-md">
        {/* Main action: start the live directly */}
        <button onClick={r.toggle} disabled={r.loading || !r.meta.stream_url} data-testid="radio-play" aria-label={r.playing ? "Pausa diretta" : "Avvia diretta"} className="h-8 w-8 shrink-0 rounded-full bg-white text-legend-red flex items-center justify-center hover:scale-105 active:scale-95 transition disabled:opacity-50">
          {r.playing ? <Pause size={16} /> : <Play size={16} className="ml-0.5" />}
        </button>
        <button onClick={r.stop} disabled={r.loading || !r.meta.stream_url} data-testid="radio-stop" aria-label="Stop" className="h-7 w-7 shrink-0 rounded-full bg-legend-ink text-white flex items-center justify-center hover:bg-black transition disabled:opacity-50">
          <Square size={13} />
        </button>

        <span className="relative flex h-2.5 w-2.5">
          <span className={`absolute inline-flex h-full w-full rounded-full bg-legend-yellow ${r.playing ? "opacity-75 animate-ping" : "opacity-0"}`} />
          <span className="relative inline-flex rounded-full h-2.5 w-2.5 bg-legend-yellow" />
        </span>
        <span className="font-display font-black uppercase text-sm tracking-wide">Radio LiveGP</span>
        <span className="font-mono text-[10px] tracking-widest bg-legend-ink text-legend-yellow px-1.5 py-0.5 rounded">LIVE</span>

        {/* Inline volume (hidden on very small screens; available in the popup) */}
        <input type="range" min="0" max="1" step="0.05" value={r.muted ? 0 : r.vol} onChange={(e) => { r.setVol(parseFloat(e.target.value)); r.setMuted(false); }} data-testid="radio-volume" aria-label="Volume" className="hidden sm:block w-20 accent-legend-yellow cursor-pointer" />

        {/* Optional: open the full player popup only if wanted */}
        <button onClick={() => setOpen((o) => !o)} data-testid="radio-expand" aria-label="Apri il player" title="Apri il player" className="ml-0.5 text-white/90 hover:text-legend-yellow">
          <ChevronDown size={16} />
        </button>
      </div>

      {r.audioEl}
      {open && <ExpandedPlayer r={r} onClose={() => setOpen(false)} />}
    </div>
  );
}
